/**
 * Crowd placement parsing + transform (runtime-handoff-v5, directive §5).
 *
 * ── The real data contract (verified by inspection, NOT the §5 assumption) ────
 * crowd_placements.json ships `format: "[x,y,z,yaw] per person (Y-up)"` —
 * position + a single yaw about world-Y per person, grouped into tiers. It does
 * NOT ship 4×4 matrices. So the runtime does the ONE composition the contract
 * implies and NOTHING else:
 *
 *     matrix = compose( position=[x,y,z], quaternion=Yaw(y-axis, yaw), scale=1 )
 *
 * The y coordinate already encodes the seat height (≈2.76 m front rows, ≈26 m
 * upper tiers). So, per the anti-floating/anti-stacking rules (§5):
 *   • NO extra seat-height offset,
 *   • NO second section transform,
 *   • NO re-rotation after composing,
 *   • the matrix is applied EXACTLY ONCE (written straight into the instance).
 *
 * The prior floating/stacked/feet-clipping bugs were runtime transform handling,
 * not the placement data — so this module is deliberately tiny and total.
 *
 * If a future handoff ships real matrices, add a branch keyed on `format` here
 * ONLY (the instancing/render code above never learns the difference).
 */
import { Matrix4, Quaternion, Vector3, Euler } from 'three';

/** One approved person: metres + a yaw (radians) about world-Y. */
export interface CrowdInstance {
  x: number;
  y: number;
  z: number;
  yaw: number;
}

export interface CrowdTier {
  name: string;
  count: number;
  instances: CrowdInstance[];
}

export interface CrowdPlacements {
  format: string;
  upAxis: string;
  totalInstances: number;
  tiers: CrowdTier[];
}

const UP = new Vector3(0, 1, 0);

/** Parse crowd_placements.json into typed tiers. Throws (fail loud, §1) on a
 *  format this module has not been proven against, so we never silently mis-place
 *  85k people. */
export function parseCrowdPlacements(json: unknown): CrowdPlacements {
  const root = json as {
    format?: string; upAxis?: string; totalInstances?: number;
    tiers?: Record<string, { count?: number; instances?: number[][] }>;
  };
  const format = root.format ?? '';
  if (!format.includes('yaw')) {
    throw new Error(
      `crowdPlacements: unexpected format "${format}". This parser is proven only ` +
        `for the "[x,y,z,yaw] per person" contract — refusing to guess (add a ` +
        `format branch before shipping matrices).`,
    );
  }
  const tiers: CrowdTier[] = [];
  let summed = 0;
  for (const [name, tier] of Object.entries(root.tiers ?? {})) {
    const rows = tier.instances ?? [];
    const instances: CrowdInstance[] = rows.map((r) => ({ x: r[0]!, y: r[1]!, z: r[2]!, yaw: r[3]! }));
    summed += instances.length;
    tiers.push({ name, count: tier.count ?? instances.length, instances });
  }
  return {
    format,
    upAxis: root.upAxis ?? 'Y',
    totalInstances: root.totalInstances ?? summed,
    tiers,
  };
}

/**
 * Compose the ONE world matrix for a person — position + Y-yaw + unit scale.
 * Writes into `out` (no allocation in hot loops) and returns it. This is applied
 * exactly once per instance; nothing downstream adds an offset or re-rotates.
 */
export function composeCrowdMatrix(inst: CrowdInstance, out: Matrix4, scratch?: {
  pos: Vector3; quat: Quaternion; scl: Vector3;
}): Matrix4 {
  const pos = scratch?.pos ?? new Vector3();
  const quat = scratch?.quat ?? new Quaternion();
  const scl = scratch?.scl ?? new Vector3();
  pos.set(inst.x, inst.y, inst.z);
  quat.setFromAxisAngle(UP, inst.yaw);
  scl.set(1, 1, 1);
  return out.compose(pos, quat, scl);
}

/** Reusable scratch so a full crowd compose allocates nothing. */
export function makeComposeScratch(): { pos: Vector3; quat: Quaternion; scl: Vector3 } {
  return { pos: new Vector3(), quat: new Quaternion(), scl: new Vector3() };
}

export interface PlacementCheck {
  ok: boolean;
  finite16: boolean;
  translationPreserved: boolean;
  rotationPreserved: boolean;
  scaleUnit: boolean;
  noSeatHeightOffset: boolean;
  reason: string | undefined;
}

/**
 * Verify a composed matrix honours the contract (directive §5 checklist):
 *   • 16 finite numbers,
 *   • decomposes back to EXACTLY the input translation (so no seat-height offset
 *     and no section transform crept in),
 *   • rotation is a pure Y-yaw equal to the input,
 *   • scale is unit.
 * Pure + allocation-light; used by the tests and the debug panel.
 */
export function checkPlacementMatrix(inst: CrowdInstance, m: Matrix4, epsilon = 1e-5): PlacementCheck {
  const finite16 = m.elements.length === 16 && m.elements.every((n) => Number.isFinite(n));
  if (!finite16) return { ok: false, finite16: false, translationPreserved: false, rotationPreserved: false, scaleUnit: false, noSeatHeightOffset: false, reason: 'matrix has non-finite elements' };

  const pos = new Vector3();
  const quat = new Quaternion();
  const scl = new Vector3();
  m.decompose(pos, quat, scl);

  const translationPreserved =
    Math.abs(pos.x - inst.x) < epsilon && Math.abs(pos.y - inst.y) < epsilon && Math.abs(pos.z - inst.z) < epsilon;
  // The y in the matrix must equal the JSON y — no added seat-height offset.
  const noSeatHeightOffset = Math.abs(pos.y - inst.y) < epsilon;
  const scaleUnit = Math.abs(scl.x - 1) < epsilon && Math.abs(scl.y - 1) < epsilon && Math.abs(scl.z - 1) < epsilon;

  // Rotation must be a pure yaw about Y equal to the input (no re-rotation).
  const euler = new Euler().setFromQuaternion(quat, 'YXZ');
  const wrap = (a: number): number => Math.atan2(Math.sin(a), Math.cos(a));
  const rotationPreserved =
    Math.abs(wrap(euler.y - inst.yaw)) < 1e-4 && Math.abs(euler.x) < 1e-4 && Math.abs(euler.z) < 1e-4;

  const ok = finite16 && translationPreserved && rotationPreserved && scaleUnit && noSeatHeightOffset;
  return {
    ok, finite16, translationPreserved, rotationPreserved, scaleUnit, noSeatHeightOffset,
    reason: ok ? undefined :
      !translationPreserved ? 'translation drifted (an offset or section transform leaked in)' :
      !rotationPreserved ? 'rotation is not the pure input yaw (re-rotated?)' :
      !scaleUnit ? 'scale is not unit' : undefined,
  };
}
